import React, { useMemo } from 'react';
import { 
    MessageSquare, Plus, Loader2, CheckCircle, Clock, 
    UploadCloud, Network, GitBranch, Cpu, ChevronRight, ChevronDown, Circle 
} from 'lucide-react';

// --- Recursive Tree Node Component ---
const PlanNode = ({ node, allNodes, level = 0 }) => {
    const isCompleted = node.status === 'done' || node.status === 'completed';
    const isProcessing = node.status === 'in progress' || node.status === 'processing';
    const isPending = node.status === 'not start' || node.status === 'idle';

    // Status Colors
    const getStatusColor = () => {
        if (isCompleted) return 'var(--primary-color)'; // Blue/Green
        if (isProcessing) return '#f59e0b'; // Amber/Yellow
        return 'var(--text-tertiary)'; // Gray
    };

    const getBgColor = () => {
        if (isCompleted) return 'var(--bg-secondary)';
        if (isProcessing) return 'rgba(245, 158, 11, 0.1)';
        return 'transparent';
    };

    return (
        <div style={{ marginLeft: level > 0 ? '20px' : '0' }}>
            {/* Node Row */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px',
                marginBottom: '4px',
                borderRadius: '6px',
                backgroundColor: getBgColor(),
                borderLeft: level > 0 ? '1px solid var(--border-color)' : 'none',
                transition: 'all 0.3s ease'
            }}>
                {/* Icon based on status */}
                <div style={{ marginRight: '8px', color: getStatusColor() }}>
                    {isCompleted && <CheckCircle size={14} />}
                    {isProcessing && <Loader2 size={14} className="animate-spin" />}
                    {isPending && <Circle size={14} />}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                    <div style={{ 
                        fontSize: '13px', 
                        fontWeight: isProcessing ? '600' : '500',
                        color: isPending ? 'var(--text-secondary)' : 'var(--text-primary)'
                    }}>
                        {node.name}
                    </div>
                    {/* Optional: Show status text for active items */}
                    {isProcessing && (
                        <div style={{ fontSize: '10px', color: '#f59e0b', marginTop: '2px' }}>
                            Processing...
                        </div>
                    )}
                </div>
            </div>

            {/* Render Children Recursively */}
            {node.children && node.children.length > 0 && (
                <div className="node-children">
                    {node.children.map(childId => {
                        const childNode = allNodes[childId];
                        if (!childNode) return null;
                        return <PlanNode key={childId} node={childNode} allNodes={allNodes} level={level + 1} />;
                    })}
                </div>
            )}
        </div>
    );
};

function RightPanel({
    chatSessions,
    currentSession,
    selectChatSession,
    handleNewChat,
    searchProgress, 
    activeTask 
}) {
    // Determine which flow to show: Active Task takes priority
    const isTaskActive = activeTask && activeTask.taskId && activeTask.status !== 'idle';
    
    // --- Tree Construction Logic ---
    // This converts the flat list of steps into a tree based on IDs (e.g., "2" is parent of "2.1")
    // OR creates a default linear tree if IDs don't follow the pattern.
    const treeData = useMemo(() => {
        if (!isTaskActive || !activeTask.steps) return null;

        const nodes = {};
        const rootChildren = [];

        // 1. Populate Nodes Map
        activeTask.steps.forEach(step => {
            // Assume the backend sends step_id as "1", "1.1", etc.
            // If backend sends integer IDs (1, 2, 3), we treat them as root level.
            // For now, let's auto-generate string IDs based on index if they don't exist
            const id = step.step_id ? String(step.step_id) : step.name; 
            
            nodes[id] = { ...step, id, children: [] };
        });

        // 2. Build Hierarchy
        Object.values(nodes).forEach(node => {
            if (node.id.includes('.')) {
                // It's a child (e.g., "2.1")
                const parentId = node.id.split('.').slice(0, -1).join('.');
                if (nodes[parentId]) {
                    nodes[parentId].children.push(node.id);
                } else {
                    // Fallback if parent not found
                    rootChildren.push(node.id);
                }
            } else {
                // It's a root item (e.g., "1", "2")
                rootChildren.push(node.id);
            }
        });

        return { nodes, rootChildren };
    }, [activeTask, isTaskActive]);


    // Helper to get title and icon based on task type
    const getTaskHeader = () => {
        if (activeTask.type === 'upload') return { title: "Upload Progress", icon: <UploadCloud size={18} /> };
        if (activeTask.type === 'taxonomy') return { title: "Taxonomy Creation", icon: <Network size={18} /> };
        if (activeTask.type === 'query_planning') return { title: "Query Planning", icon: <GitBranch size={18} /> };
        if (activeTask.type === 'query_processing') return { title: "Processing Plan", icon: <Cpu size={18} /> };
        return { title: "Task Progress", icon: null };
    };

    const taskHeader = getTaskHeader();

    // Condition to use Tree View vs Linear View
    const useTreeView = activeTask.type === 'query_processing';

    return (
        <section className="progress-panel" style={{ height: '100vh', overflowY: 'auto', borderLeft: '1px solid var(--divider-color)', background: 'var(--bg-sidebar)', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '300px' }}>
            
            {/* Chat Sessions Card */}
            <div className="card sidebar-card session-card">
                <div className="card-header">Chat Sessions</div>
                <div className="session-list-container">
                    {chatSessions.length === 0 ? (
                         <div style={{padding: '10px', fontSize: '12px', color: 'var(--text-tertiary)'}}>No active sessions</div>
                    ) : (
                        chatSessions.map(session => (
                            <div 
                                key={session.id} 
                                className={`list-item ${currentSession?.id === session.id ? 'active' : ''}`}
                                onClick={() => selectChatSession(session)}
                            >
                                <div className="list-item-left">
                                    <MessageSquare size={14} className="item-icon" />
                                    <div className="session-info">
                                        <div className="item-text">{session.name}</div>
                                        <div className="item-sub" style={{ fontSize: '10px' }}>{session.kbNameUsed}</div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
                <div className="card-footer-actions">
                    <button className="action-btn-outline new-chat-btn" onClick={handleNewChat}>
                        <Plus size={14} />
                        <span>New chat</span>
                    </button>
                </div>
            </div>

            {/* Task Execution / Progress Flow */}
            <div className="card-header">
                {isTaskActive ? taskHeader.title : "Working Flow"}
            </div>

            {/* Simple Metrics Header */}
            <div className="metrics-grid">
                {isTaskActive ? (
                    <>
                        <div className="metric-box">
                            <div className="metric-val">{activeTask.steps?.length || 0}</div>
                            <div className="metric-lbl">Steps</div>
                        </div>
                        <div className="metric-box">
                            <div className="metric-val active">
                                {taskHeader.icon}
                            </div>
                            <div className="metric-lbl">Type</div>
                        </div>
                        <div className="metric-box">
                            <div className="metric-val">
                                {activeTask.status === 'completed' ? '100%' : '...'}
                            </div>
                            <div className="metric-lbl">Status</div>
                        </div>
                    </>
                ) : (
                    // Placeholder metrics when idle
                    <>
                        <div className="metric-box">
                            <div className="metric-val">0</div>
                            <div className="metric-lbl">Steps</div>
                        </div>
                        <div className="metric-box">
                            <div className="metric-val">Idle</div>
                            <div className="metric-lbl">State</div>
                        </div>
                        <div className="metric-box">
                            <div className="metric-val">-</div>
                            <div className="metric-lbl">Results</div>
                        </div>
                    </>
                )}
            </div>

            {/* Steps Visualization - Conditional Rendering */}
            <div className="flow-card" style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                minHeight: 0, // Required for nested flex scrolling
                overflow: 'hidden' 
            }}>
                <div className="flow-header">
                    <div className="flow-title">{isTaskActive ? "Execution Trace" : "Task Execution"}</div>
                    <div className="flow-status" style={{
                         backgroundColor: (isTaskActive || searchProgress?.status === 'processing') ? 'var(--primary-light)' : '#f5f5f5',
                         color: (isTaskActive || searchProgress?.status === 'processing') ? 'var(--primary-color)' : 'var(--text-tertiary)'
                    }}>
                        {isTaskActive 
                            ? (activeTask.status === 'completed' ? 'Done' : 'Running') 
                            : 'Ready'
                        }
                    </div>
                </div>
                
                <div className="flow-steps" style={{ 
                    padding: '12px', 
                    overflowY: 'auto', // Enable scrolling here
                    flex: 1 // Fill remaining space in card
                }}>
                    {isTaskActive && treeData ? (
                        // If it's Query Processing, use the recursive Tree View
                        useTreeView ? (
                            treeData.rootChildren.map(nodeId => (
                                <PlanNode 
                                    key={nodeId} 
                                    node={treeData.nodes[nodeId]} 
                                    allNodes={treeData.nodes} 
                                />
                            ))
                        ) : (
                            // Otherwise (Upload, Taxonomy, Planning), use the standard linear view
                            activeTask.steps.map((step, idx) => (
                                <React.Fragment key={idx}>
                                    <div className={`flow-step ${step.status === 'done' || step.status === 'completed' ? 'completed' : step.status === 'in progress' ? 'active' : ''}`}>
                                        <div className="step-icon">
                                            {step.status === 'done' || step.status === 'completed' ? <CheckCircle size={12} /> : 
                                             step.status === 'in progress' ? <Loader2 size={12} className="animate-spin" /> : 
                                             <Clock size={12} />}
                                        </div>
                                        <div className="step-body">
                                            <div className="step-title">{step.name}</div>
                                            <div className="step-desc" style={{textTransform: 'capitalize'}}>{step.status}</div>
                                        </div>
                                    </div>
                                    {idx < activeTask.steps.length - 1 && <div className="flow-connector"></div>}
                                </React.Fragment>
                            ))
                        )
                    ) : (
                        <div style={{ padding: '10px', fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
                            Waiting for tasks...
                        </div>
                    )}
                </div>
            </div>

        </section>
    );
}

export default RightPanel;